import { EVENT_STATUSES, isEventStatus, type EventStatus } from '@inventohub/shared';

/**
 * Event list choices for one signed-in person, on this browser only (F-23).
 * The key is their id. The value is the six filters plus a UTC expiry instant.
 * ADR-003: this record is not a credential.
 */

/** 14 × 24 hours. Not a calendar date. */
export const EVENT_FILTER_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const EVENT_FILTER_KEY_PREFIX = 'inventohub.event-filters.';

const USER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RECORD_KEYS = [
  'viewAll',
  'organizationId',
  'statuses',
  'confirmed',
  'staffUserId',
  'missingInventory',
  'expiresAt',
] as const;

export type ConfirmedFilter = '' | 'Yes' | 'No';

export interface EventFilterSelection {
  viewAll: boolean;
  organizationId: string;
  statuses: EventStatus[];
  confirmed: ConfirmedFilter;
  staffUserId: string;
  missingInventory: boolean;
}

export const DEFAULT_EVENT_FILTERS: EventFilterSelection = {
  viewAll: false,
  organizationId: '',
  statuses: [],
  confirmed: '',
  staffUserId: '',
  missingInventory: false,
};

export interface EventFilterStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function standingUserId(
  userId: string | null | undefined,
  codeSession: boolean,
): string | null {
  if (codeSession || !userId || !USER_ID_PATTERN.test(userId)) return null;
  return userId;
}

export function eventFilterStorageKey(userId: string): string | null {
  if (!USER_ID_PATTERN.test(userId)) return null;
  return `${EVENT_FILTER_KEY_PREFIX}${userId}`;
}

export function confirmedQueryValue(value: ConfirmedFilter): 'true' | 'false' | null {
  if (value === 'Yes') return 'true';
  if (value === 'No') return 'false';
  return null;
}

export function sameEventFilters(a: EventFilterSelection, b: EventFilterSelection): boolean {
  return (
    a.viewAll === b.viewAll &&
    a.organizationId === b.organizationId &&
    a.staffUserId === b.staffUserId &&
    a.confirmed === b.confirmed &&
    a.missingInventory === b.missingInventory &&
    a.statuses.length === b.statuses.length &&
    a.statuses.every((status, index) => status === b.statuses[index])
  );
}

export function eventFiltersNeedOptionCheck(filters: EventFilterSelection): boolean {
  return filters.organizationId !== '' || filters.staffUserId !== '';
}

/**
 * Ids from a real options payload. A list-shaped body (no organization or staff
 * arrays) returns null so the page does not throw while correcting filters.
 */
export function knownOptionIds(
  body: unknown,
): { organizationIds: string[]; staffUserIds: string[] } | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as { organizations?: unknown; users?: unknown };
  if (!Array.isArray(record.organizations) || !Array.isArray(record.users)) return null;
  const organizationIds: string[] = [];
  for (const org of record.organizations) {
    if (typeof org !== 'object' || org === null) continue;
    const id = (org as { id?: unknown }).id;
    if (typeof id === 'string') organizationIds.push(id);
  }
  const staffUserIds: string[] = [];
  for (const member of record.users) {
    if (typeof member !== 'object' || member === null) continue;
    const userId = (member as { userId?: unknown }).userId;
    if (typeof userId === 'string') staffUserIds.push(userId);
  }
  return { organizationIds, staffUserIds };
}

/** Unknown organization or staff becomes Any. Other choices stay. Same object if nothing changed. */
export function applyKnownOptions(
  filters: EventFilterSelection,
  organizationIds: readonly string[],
  staffUserIds: readonly string[],
): EventFilterSelection {
  const organizationId =
    filters.organizationId !== '' && !organizationIds.includes(filters.organizationId)
      ? ''
      : filters.organizationId;
  const staffUserId =
    filters.staffUserId !== '' && !staffUserIds.includes(filters.staffUserId)
      ? ''
      : filters.staffUserId;
  if (organizationId === filters.organizationId && staffUserId === filters.staffUserId) {
    return filters;
  }
  return { ...filters, organizationId, staffUserId };
}

export function browserEventFilterStorage(): EventFilterStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function readEventFilters(
  storage: EventFilterStorage,
  userId: string,
  nowMs: number,
): EventFilterSelection | null {
  const key = eventFilterStorageKey(userId);
  if (!key) return null;
  const raw = readRaw(storage, key);
  if (raw === null) return null;
  const parsed = parseRecord(raw, nowMs);
  if (parsed === 'drop') {
    dropKey(storage, key);
    return null;
  }
  return parsed;
}

export function writeEventFilters(
  storage: EventFilterStorage,
  userId: string,
  filters: EventFilterSelection,
  nowMs: number,
): boolean {
  const key = eventFilterStorageKey(userId);
  if (!key) return false;
  const record = {
    viewAll: filters.viewAll,
    organizationId: filters.organizationId,
    statuses: canonicalStatuses(filters.statuses),
    confirmed: filters.confirmed === 'Yes' || filters.confirmed === 'No' ? filters.confirmed : '',
    staffUserId: filters.staffUserId,
    missingInventory: filters.missingInventory,
    expiresAt: new Date(nowMs + EVENT_FILTER_TTL_MS).toISOString(),
  };
  try {
    storage.setItem(key, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/** Moves the expiry of an existing record. Does nothing when there is nothing to keep. */
export function touchEventFilterExpiry(
  storage: EventFilterStorage,
  userId: string,
  nowMs: number,
): boolean {
  const filters = readEventFilters(storage, userId, nowMs);
  if (!filters) return false;
  return writeEventFilters(storage, userId, filters, nowMs);
}

function canonicalStatuses(values: readonly EventStatus[]): EventStatus[] {
  const picked = new Set(values);
  return EVENT_STATUSES.filter((status) => picked.has(status));
}

function readRaw(storage: EventFilterStorage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function dropKey(storage: EventFilterStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Defaults still apply for this visit. The page shows no storage error.
  }
}

type Parsed = EventFilterSelection | 'drop';

function parseRecord(raw: string, nowMs: number): Parsed {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return 'drop';
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return 'drop';
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== RECORD_KEYS.length || RECORD_KEYS.some((key) => !keys.includes(key))) {
    return 'drop';
  }
  if (typeof record.viewAll !== 'boolean') return 'drop';
  if (typeof record.organizationId !== 'string') return 'drop';
  if (typeof record.staffUserId !== 'string') return 'drop';
  if (typeof record.missingInventory !== 'boolean') return 'drop';
  if (!('confirmed' in record)) return 'drop';
  const statuses = parseStatuses(record.statuses);
  if (!statuses) return 'drop';
  const expiresAt = parseExpiry(record.expiresAt);
  if (expiresAt === null) return 'drop';
  if (expiresAt <= nowMs) return 'drop';
  const confirmed: ConfirmedFilter =
    record.confirmed === 'Yes' || record.confirmed === 'No' ? record.confirmed : '';
  return {
    viewAll: record.viewAll,
    organizationId: record.organizationId,
    statuses,
    confirmed,
    staffUserId: record.staffUserId,
    missingInventory: record.missingInventory,
  };
}

function parseStatuses(value: unknown): EventStatus[] | null {
  if (!Array.isArray(value)) return null;
  const picked = new Set<EventStatus>();
  for (const entry of value) {
    if (isEventStatus(entry)) picked.add(entry);
  }
  return EVENT_STATUSES.filter((status) => picked.has(status));
}

function parseExpiry(value: unknown): number | null {
  if (typeof value !== 'string' || !value.includes('T')) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return ms;
}
